/**
 * Meta Pixel + Signals Gateway helper.
 *
 * Loads both the standard Meta Pixel and the Signals Gateway (server-side
 * Conversions API proxy). The gateway intercepts all `fbq('track', ...)`
 * calls and forwards them server-side for deduplication and improved
 * attribution — especially useful against ad blockers and iOS privacy.
 *
 * Required env vars (production only — safe no-op when missing):
 *   VITE_FB_PIXEL_ID        – Meta Pixel ID
 *   VITE_SG_GATEWAY_ID      – Signals Gateway dataset ID
 *   VITE_SG_TRACKING_HOST   – Signals Gateway host (e.g. https://trckg.mediloop.ai)
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
    xbq?: (...args: any[]) => void;
    cbq?: (...args: any[]) => void;
    _cbq?: (...args: any[]) => void;
  }
}

const PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID as string | undefined;
const GATEWAY_ID = import.meta.env.VITE_SG_GATEWAY_ID as string | undefined;
const TRACKING_HOST = import.meta.env.VITE_SG_TRACKING_HOST as string | undefined;

/**
 * URL of the Conversions API proxy hosted on apps/web. Cross-origin POSTs
 * from apps/app go here; the route forwards to graph.facebook.com using
 * the server-side access token. Falls back to mediloop.ai in production.
 */
const CAPI_URL =
  (import.meta.env.VITE_CAPI_URL as string | undefined) ||
  'https://mediloop.ai/api/conversion';

/**
 * True when running on a host that must never fire production tracking:
 *   • staging-* deploys — aliased onto the production build, so env vars
 *     alone can't distinguish them from mediloop.ai
 *   • local dev (localhost / loopback) — .env.local carries the real pixel
 *     IDs so `vite dev` would otherwise fire the production pixel. Spotted
 *     2026-07-06 in Meta Events Manager as a "localhost" website on the
 *     production dataset (53 events / 28 days).
 * Exported so redditPixel.ts shares the same guard.
 */
export function isBlockedTrackingHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  return (
    host.includes('staging') ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    host === '0.0.0.0' ||
    host.endsWith('.localhost')
  );
}

/**
 * Read Meta's `_fbp` and `_fbc` cookies set by fbevents.js — these are
 * required for the Conversions API to attribute server-side events back
 * to the same browser session that fired the client-side pixel. Without
 * them dedup still works (via event_id), but match quality is lower.
 */
function readMetaCookie(name: '_fbp' | '_fbc'): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Read the `mediloop_vid` visitor-id cookie set by the marketing site
 * middleware. Scoped to `.mediloop.ai` so it survives the subdomain
 * hop from mediloop.ai → app.mediloop.ai, which is what we want — it
 * stitches an anonymous browser to the eventual logged-in session.
 *
 * Used as a fallback `external_id` for Meta CAPI when there isn't a
 * Supabase user.id available (e.g. early signup flow). When we DO have
 * a user.id, the caller should pass that as `externalId` instead.
 */
export function readVisitorId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|; )mediloop_vid=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Fire a server-side Conversions API event via the apps/web /api/conversion
 * proxy. Always called alongside the client-side `fbq('track', ...)` with
 * the same `event_id` so Meta can dedup the two sources. Failures are
 * swallowed — server-side CAPI is a redundancy layer, not the primary
 * tracking path.
 */
async function sendCapiEvent(params: {
  event_name: string;
  event_id: string;
  custom_data?: Record<string, unknown>;
  user_email?: string;
  user_phone?: string;
  /** Stable user identifier — typically supabase user.id. Falls back to the
   *  cross-subdomain visitor_id cookie when caller doesn't supply one. */
  external_id?: string;
}): Promise<void> {
  // Host guard — don't forward CAPI events from staging or local dev into
  // the production dataset (the pixel itself is also disabled there).
  if (isBlockedTrackingHost()) return;
  try {
    await fetch(CAPI_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: params.event_name,
        event_id: params.event_id,
        event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
        custom_data: params.custom_data || {},
        fbp: readMetaCookie('_fbp'),
        fbc: readMetaCookie('_fbc'),
        user_email: params.user_email,
        user_phone: params.user_phone,
        // Caller-supplied external_id (e.g. supabase user.id) takes
        // priority; otherwise fall back to the visitor_id cookie so we
        // always send SOMETHING stable Meta can match on.
        external_id: params.external_id || readVisitorId(),
      }),
      // Don't block navigation if the request is in flight
      keepalive: true,
    });
  } catch {
    // Server-side CAPI is best-effort — swallow errors so they don't
    // surface to users or break navigation.
  }
}

/* -------------------------------------------------- */
/*  SHA-256 hashing (Web Crypto API — zero dependencies)
/* -------------------------------------------------- */

/**
 * Hash an email address with SHA-256 for Meta Advanced Matching.
 * Meta requires: lowercase, trimmed, hex-encoded SHA-256.
 * Returns `undefined` if hashing is unavailable (e.g. non-secure context).
 */
async function hashEmail(email: string): Promise<string | undefined> {
  try {
    const normalized = email.trim().toLowerCase();
    const encoded = new TextEncoder().encode(normalized);
    const buffer = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return undefined;
  }
}

/* -------------------------------------------------- */
/*  Pixel + Gateway bootstrap
/* -------------------------------------------------- */

/**
 * Load the Meta Pixel and Signals Gateway scripts, then fire PageView.
 * Call this once at app startup (e.g. in main.tsx).
 * Does nothing if VITE_FB_PIXEL_ID is not set.
 *
 * @param email – Optional user email for Advanced Matching.
 *   If provided the email is SHA-256 hashed before being sent to Meta.
 */
/* eslint-disable prefer-rest-params, prefer-spread, @typescript-eslint/no-unused-expressions */
export async function initPixel(email?: string): Promise<void> {
  if (!PIXEL_ID) return;
  // Never run on staging or local-dev hosts — even when the build has
  // VITE_FB_PIXEL_ID set (as .env.local does), this keeps non-production
  // traffic out of the production dataset.
  if (isBlockedTrackingHost()) return;
  if (typeof window.fbq === 'function') return; // already loaded

  // ---- 1. Bootstrap Signals Gateway (cbq) ----
  if (GATEWAY_ID && TRACKING_HOST) {
    const nn: any = (window.cbq = function () {
      nn.initialized
        ? nn.apply(window.cbq, arguments)
        : nn.queue.push(arguments);
    });
    if (!window._cbq) window._cbq = nn;
    nn.push = nn;
    nn.loaded = true;
    nn.version = '2.0';
    nn.queue = [];

    const sgScript = document.createElement('script');
    sgScript.async = true;
    sgScript.src = `${TRACKING_HOST}/sdk/${GATEWAY_ID}/events.js`;
    const firstEl = document.getElementsByTagName('script')[0];
    firstEl.parentNode?.insertBefore(sgScript, firstEl);
  }

  // ---- 2. Bootstrap Meta Pixel (fbq) with gateway interception ----
  // If gateway is active, preserve any existing fbq as xbq, then create
  // a new fbq that forks 'track' calls to both Meta and the gateway.
  if (window.fbq) window.xbq = window.fbq;

  const n: any = (window.fbq = function () {
    const args = arguments;
    if (
      GATEWAY_ID &&
      TRACKING_HOST &&
      typeof args[0] === 'string' &&
      args[0].startsWith('track')
    ) {
      // Always fire to the Meta Pixel (browser).
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      // Fork to the Signals Gateway (server-side mirror) for every event
      // EXCEPT the conversion events we already send server-side elsewhere.
      // The gateway and our CAPI legs both post to the same Meta dataset with
      // the same event_id, but Meta's pixel↔CAPI dedup is pairwise and can't
      // collapse multiple server copies — so each extra server leg inflates
      // counted conversions. We keep exactly one server leg per event:
      //   • Purchase             → Stripe webhook CAPI (apps/server)
      //   • InitiateCheckout     → /api/conversion CAPI (apps/web)
      //   • CompleteRegistration → /api/conversion CAPI (2026-07-02 — was
      //     still double-sending gateway + CAPI, same pattern as the others)
      //   • trackCustom events   → /api/conversion CAPI via trackCustomEvent
      //     (2026-07-02 — every custom event has its own sendCapiEvent leg)
      // and leave the browser pixel as the other half of the dedup pair.
      const skipGatewayFork =
        args[0] === 'trackCustom' ||
        args[1] === 'Purchase' ||
        args[1] === 'InitiateCheckout' ||
        args[1] === 'CompleteRegistration';
      if (window.cbq && !skipGatewayFork) {
        (window.cbq as any).initialized
          ? (window.cbq as any).apply(window.cbq, args)
          : (window.cbq as any).queue.push(args);
      }
    } else {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    }
  });

  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = window.xbq ? (window.xbq as any).queue || [] : [];

  const fbScript = document.createElement('script');
  fbScript.async = true;
  fbScript.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  first.parentNode?.insertBefore(fbScript, first);

  // ---- 3. Initialize both pixels ----

  // Build Advanced Matching payload with hashed email
  const userData: Record<string, string> = {};
  if (email) {
    const hashed = await hashEmail(email);
    if (hashed) userData.em = hashed;
  }
  // Always attach the cross-subdomain visitor id as external_id so even
  // pre-auth PageViews carry a stable match key. Post-login, the stronger
  // supabase user.id + email arrive via setPixelAdvancedMatching().
  // (2026-07-06: PageView EMQ was 6.1/10 — external_id only 80%, em 0%.)
  const visitorId = readVisitorId();
  if (visitorId) userData.external_id = visitorId;

  // Meta Pixel init + PageView
  window.fbq('init', PIXEL_ID, userData);
  window.fbq('track', 'PageView');

  // Signals Gateway init + PageView
  if (GATEWAY_ID && TRACKING_HOST && typeof window.cbq === 'function') {
    window.cbq('setHost', TRACKING_HOST);
    window.cbq('init', GATEWAY_ID);
    window.cbq('set', 'integrationMethod', 'forkFromSnippetCode');
    window.cbq('track', 'PageView');
  }
}
/* eslint-enable prefer-rest-params, prefer-spread, @typescript-eslint/no-unused-expressions */

/**
 * Update Meta Advanced Matching once the authenticated user is known.
 * Re-calling `fbq('init', ...)` with user_data attaches the identifiers
 * to all SUBSEQUENT events in this page session — Meta's documented
 * pattern for late-arriving identity. Called from PostHogIdentifier on
 * every auth change.
 *
 * Why: initPixel() runs at app boot, before auth resolves, so portal
 * events fired with no email even for logged-in users. PageView EMQ was
 * 6.1/10 on 2026-07-06 (em 0%, external_id 80%) — em is Meta's highest-
 * weight match key, and better matching directly improves signup-ad
 * optimization.
 */
export async function setPixelAdvancedMatching(params: {
  /** User email — SHA-256 hashed before being handed to fbq. */
  email?: string;
  /** Stable user id (supabase user.id). Falls back to the visitor cookie. */
  externalId?: string;
}): Promise<void> {
  if (!PIXEL_ID) return;
  if (isBlockedTrackingHost()) return;
  if (typeof window.fbq !== 'function') return;

  const userData: Record<string, string> = {};
  if (params.email) {
    const hashed = await hashEmail(params.email);
    if (hashed) userData.em = hashed;
  }
  const extId = params.externalId || readVisitorId();
  if (extId) userData.external_id = extId;
  if (!Object.keys(userData).length) return;

  window.fbq('init', PIXEL_ID, userData);
}

/* -------------------------------------------------- */
/*  Purchase tracking
/* -------------------------------------------------- */

interface PurchaseParams {
  /** Dollar amount (e.g. 59.00) */
  value: number;
  currency?: string;
  /** Plan type: 'one_off' | 'annual' | 'addon_bill' | 'upgrade_to_annual' */
  contentName: string;
  /** Unique transaction ID (checkout_ref or Stripe session_id) */
  eventID: string;
  /** Optional user email — will be SHA-256 hashed before sending to Meta */
  email?: string;
  /** Kept for the callers' sake; the server-side Purchase legs come from
   *  the Stripe webhook, which has its own identity, so nothing browser-side
   *  reads this any more (2026-09-04). */
  externalId?: string;
}

/**
 * Fire a Meta Pixel Purchase event with deduplication.
 *
 * Uses sessionStorage to prevent double-fires on page refresh,
 * and passes eventID so Meta can deduplicate server-side too.
 * If an email is provided it is SHA-256 hashed and included in
 * the user_data payload for improved attribution.
 *
 * The Signals Gateway automatically intercepts `fbq('track', ...)`
 * calls and forwards them server-side — no extra work needed here.
 *
 * No-op if pixel is not loaded (local/staging/ad blockers).
 */
export async function trackPurchase({ value, currency = 'USD', contentName, eventID, email }: PurchaseParams): Promise<void> {
  // 🧪 Guard (2026-07-02): eventID is always the Stripe checkout session id.
  // cs_test_* = Stripe test mode (QA/staging) — never fire real Meta
  // Purchases for test checkouts. Mirrors the webhook's session.livemode
  // gate so browser+server stay symmetric.
  if (eventID?.startsWith('cs_test_')) return;

  // Guard: already tracked this transaction in this browser session
  const storageKey = `fb_purchase_tracked_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable (e.g. private browsing) — continue anyway
  }

  // Guard: pixel not loaded (no PIXEL_ID, ad blockers, script failure)
  if (typeof window.fbq !== 'function') return;

  // Build user_data with hashed email for Advanced Matching
  const userData: Record<string, string> = {};
  if (email) {
    const hashed = await hashEmail(email);
    if (hashed) userData.em = hashed;
  }

  window.fbq('track', 'Purchase', {
    value,
    currency,
    content_name: contentName,
    content_ids: [eventID],
    content_type: 'product',
    num_items: 1,
  }, { eventID, ...(Object.keys(userData).length ? { user_data: userData } : {}) });

  // No /api/conversion POST for Purchase any more (2026-09-04). It existed
  // only to trigger the GA4 Measurement Protocol purchase, and that now
  // fires from the Stripe webhook with the GA ids captured before checkout,
  // so the GA4 purchase no longer depends on this page loading or on the
  // pixel being present. Meta and Reddit server legs also come from the
  // webhook; the browser calls above dedupe against them on the session id.

  // Mark as tracked so refreshes don't re-fire
  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Silently fail if storage is full/unavailable
  }
}

/* -------------------------------------------------- */
/*  Other standard / custom events
/* -------------------------------------------------- */

/**
 * Fire a Meta InitiateCheckout event with deduplication. Used for
 * in-app re-engagement — addon bill purchases, EOB→Per Bill upgrades,
 * one_off→Annual upgrades. Pairs with the eventual Purchase fire on the
 * Stripe success URL.
 */
export async function trackInitiateCheckout(params: {
  /** Unique ID for dedup */
  eventID: string;
  /** Dollar amount the user is about to pay */
  value: number;
  currency?: string;
  /** Plan slug (e.g. 'addon_bill', 'upgrade_to_annual', 'eob_to_one_off') */
  contentName: string;
  email?: string;
  /** Supabase user.id when known — boosts cross-event matching. */
  externalId?: string;
}): Promise<void> {
  const { eventID, value, currency = 'USD', contentName, email, externalId } = params;
  const storageKey = `fb_initiate_checkout_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable — continue
  }

  const userData: Record<string, string> = {};
  if (email) {
    const hashed = await hashEmail(email);
    if (hashed) userData.em = hashed;
  }

  if (typeof window.fbq === 'function') {
    window.fbq(
      'track',
      'InitiateCheckout',
      { value, currency, content_name: contentName },
      { eventID, ...(Object.keys(userData).length ? { user_data: userData } : {}) },
    );
  }

  void sendCapiEvent({
    event_name: 'InitiateCheckout',
    event_id: eventID,
    custom_data: { value, currency, content_name: contentName },
    user_email: email,
    external_id: externalId,
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Silently fail if storage is full/unavailable
  }
}

/**
 * Fire a Meta CompleteRegistration event with deduplication. Used after
 * a customer finishes the OTP signup flow. Meta values registration as a
 * "soft conversion" that's much more frequent than Purchase, which lets
 * its delivery algorithm optimize ad spend faster.
 */
export async function trackCompleteRegistration(params: {
  /** Unique ID for dedup — typically the user's Supabase ID or session ID */
  eventID: string;
  /** Optional email for Advanced Matching — SHA-256 hashed before sending */
  email?: string;
  /** Stable user id (supabase user.id) for the CAPI leg's external_id —
   *  stronger than the visitor-cookie fallback for the signup event Meta
   *  optimizes ad delivery on. */
  externalId?: string;
}): Promise<void> {
  const { eventID, email, externalId } = params;
  const storageKey = `fb_registration_tracked_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable — continue
  }

  const userData: Record<string, string> = {};
  if (email) {
    const hashed = await hashEmail(email);
    if (hashed) userData.em = hashed;
  }

  if (typeof window.fbq === 'function') {
    window.fbq(
      'track',
      'CompleteRegistration',
      { content_name: 'mediloop_signup' },
      { eventID, ...(Object.keys(userData).length ? { user_data: userData } : {}) },
    );
  }

  void sendCapiEvent({
    event_name: 'CompleteRegistration',
    event_id: eventID,
    custom_data: { content_name: 'mediloop_signup' },
    user_email: email,
    external_id: externalId,
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Silently fail if storage is full/unavailable
  }
}

/**
 * Fire a Meta custom event (e.g. BillUploaded, EobUploaded). Custom events
 * support lookalike audiences just like standard events but require an exact
 * name match between client (fbq trackCustom) and server (CAPI). The proxy's
 * allowed-events list controls which custom names are accepted.
 */
export async function trackCustomEvent(params: {
  name:
    | 'BillUploaded'
    | 'EobUploaded'
    | 'EobReportViewed'
    | 'EobAnnualUpgradeStarted'
    | 'SubscriptionCancelled';
  /** Unique ID for dedup */
  eventID: string;
  /** Optional structured data — passed to both pixel and CAPI */
  customData?: Record<string, unknown>;
  /** Optional email for Advanced Matching */
  email?: string;
}): Promise<void> {
  const { name, eventID, customData, email } = params;
  const storageKey = `fb_custom_${name}_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable — continue
  }

  const userData: Record<string, string> = {};
  if (email) {
    const hashed = await hashEmail(email);
    if (hashed) userData.em = hashed;
  }

  if (typeof window.fbq === 'function') {
    window.fbq(
      'trackCustom',
      name,
      customData || {},
      { eventID, ...(Object.keys(userData).length ? { user_data: userData } : {}) },
    );
  }

  void sendCapiEvent({
    event_name: name,
    event_id: eventID,
    custom_data: customData,
    user_email: email,
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Silently fail if storage is full/unavailable
  }
}
